/**
 * SourcesAndSendersPanel — Settings panel for:
 *   • Sources (channels that send orders: Teleflora, FTD, Phone, Stripe, etc.)
 *   • Sending Florists (List A — user-curated directory with product URLs)
 *   • AI Mappings  (List B — raw AI-extracted subsource strings → List A entries)
 *
 * Kept in one panel since these concepts cascade: a Sending Florist advertises
 * which sources it participates in, and orders are categorized by source.
 */
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Search, Pencil, Trash2, X, Flower2, Link2, ArrowRight, ArrowUpDown, Radio, ChevronDown, AlertTriangle, CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { getMappingOrders } from '@/api/sources';
import { useSourcesCatalog } from '@/hooks/useSourcesCatalog';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { API } from '@/config/constants';
import { unconfirmedDiagnostic } from '@/utils/sourcesDiagnostics';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { format, subDays, parseISO } from 'date-fns';
import DeductionRulesBreakout from '@/components/settings/DeductionRulesBreakout';
import OrderDetailModal from '@/components/OrderDetailModal';
import RangeCalendar from '@/components/RangeCalendar';

// An opening owns its submitted revision, not every later edit in this form.
function useOwnedForm(controller, empty) {
  const [form, replaceForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const lifetime = useRef({ active: true, opening: 0, revision: 0 });
  useLayoutEffect(() => {
    const owner = lifetime.current;
    owner.active = true;
    return () => { owner.active = false; ++owner.opening; };
  }, []);
  const setForm = update => { ++lifetime.current.revision; replaceForm(update); };
  const resetForm = () => {
    ++lifetime.current.opening;
    ++lifetime.current.revision;
    replaceForm(empty()); setEditingId(null); setShowForm(false);
  };
  const open = (id, value) => {
    ++lifetime.current.opening;
    ++lifetime.current.revision;
    replaceForm(value); setEditingId(id); setShowForm(true);
  };
  const capture = () => {
    const { opening, revision } = lifetime.current;
    const current = (includeRevision = true) => lifetime.current.active && controller.owns()
      && lifetime.current.opening === opening && (!includeRevision || lifetime.current.revision === revision);
    return { id: editingId, payload: JSON.parse(JSON.stringify(form)), current,
      acceptId(id) { if (current(false) && !editingId) setEditingId(id); } };
  };
  return { form, setForm, editingId, showForm, setShowForm, resetForm, open, capture };
}

function useDeleteArm() {
  const [armed, setArmed] = useState(null);
  const current = useRef(null);
  const timer = useRef(null);
  const clear = () => { clearTimeout(timer.current); current.current = null; setArmed(null); };
  useEffect(() => () => clearTimeout(timer.current), []);
  const confirm = id => {
    if (current.current === id) { clear(); return true; }
    clearTimeout(timer.current); current.current = id; setArmed(id);
    timer.current = setTimeout(clear, 3000);
    return false;
  };
  return [armed, confirm, clear];
}

function useTabOwner(controller) {
  const active = useRef(true);
  useLayoutEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  return () => active.current && controller.owns();
}

function reportMutation(result, accepted) {
  if (result.kind === 'accepted' && ['Source already deleted', 'Already deleted'].includes(result.value?.message)) toast.info('The record is currently absent. This does not establish earlier reassignment or mapping-release effects.');
  else if (result.kind === 'accepted') toast.success(accepted);
  else if (result.kind === 'rejected') toast.error(typeof result.detail === 'string' ? result.detail : 'The change was refused. Review current data before trying again.');
  // TCA-26102604. The popup carries the detail, but it only appears when the
  // record is in the list to be shown; this is the signal when it is not.
  else if (result.kind === 'unknown') toast.error("We couldn't confirm that change saved. Check the list before entering it again.");
}

function isSourceDependencyRefusal(result, id) {
  const detail = result.detail;
  const counts = detail?.dependents;
  const fields = ['orders_by_id', 'orders_by_name', 'products', 'florists', 'mappings', 'total'];
  return result.kind === 'rejected' && result.status === 409 && detail?.message === 'Source has dependents'
    && counts?.source_id === id && typeof counts.source_name === 'string'
    && fields.every(field => Number.isSafeInteger(counts[field]) && counts[field] >= 0)
    && counts.total > 0 && counts.total === counts.orders_by_id + counts.orders_by_name + counts.products + counts.florists;
}

/* ── Sources tab (order channels) ────────────────────────── */
const ReassignBeforeDeleteDialog = ({ open, source, dependents, otherSources, onClose, onConfirm, working }) => {
  const [mode, setMode] = useState('transfer');
  const [targetId, setTargetId] = useState('');

  // Reset whenever the dialog opens for a different source
  useEffect(() => {
    if (open) {
      setMode(otherSources.length > 0 ? 'transfer' : 'unassign');
      setTargetId('');
    }
  }, [open, source?.id, otherSources.length]);

  if (!source) return null;

  const counts = dependents || {};
  const lines = [];
  if (counts.orders_by_id || counts.orders_by_name) {
    lines.push(`${(counts.orders_by_id || 0) + (counts.orders_by_name || 0)} order${((counts.orders_by_id || 0) + (counts.orders_by_name || 0)) !== 1 ? 's' : ''}`);
  }
  if (counts.products) lines.push(`${counts.products} product${counts.products !== 1 ? 's' : ''}`);
  if (counts.florists) lines.push(`${counts.florists} sending florist${counts.florists !== 1 ? 's' : ''}`);

  const canConfirm =
    !working &&
    (mode === 'unassign' || (mode === 'transfer' && otherSources.some(item => item.id === targetId)));

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm({ mode, target_source_id: mode === 'transfer' ? targetId : undefined });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="source-reassign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Delete "{source.name}"?
          </DialogTitle>
          <DialogDescription>
            This source is referenced by <strong>{lines.join(', ')}</strong>. Choose what to do with them before the source is removed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {/* Transfer */}
          <label
            className={`flex flex-col gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${
              mode === 'transfer' ? 'border-primary bg-primary/5' : 'border-stone-200 hover:bg-stone-50'
            } ${otherSources.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid="reassign-mode-transfer"
          >
            <div className="flex items-center gap-2">
              <input
                type="radio"
                name="reassign-mode"
                value="transfer"
                checked={mode === 'transfer'}
                onChange={() => setMode('transfer')}
                disabled={otherSources.length === 0}
              />
              <span className="text-sm font-medium text-stone-900">Transfer to another source</span>
            </div>
            <p className="text-xs text-stone-500 ml-6">
              Move all dependent orders, products, and florists to a different source.
            </p>
            {mode === 'transfer' && otherSources.length > 0 && (
              <select
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                className="ml-6 mt-1 rounded-md border border-stone-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400"
                data-testid="reassign-target-select"
              >
                <option value="">Select target source…</option>
                {otherSources.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            )}
          </label>

          {/* Unassign */}
          <label
            className={`flex flex-col gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${
              mode === 'unassign' ? 'border-primary bg-primary/5' : 'border-stone-200 hover:bg-stone-50'
            }`}
            data-testid="reassign-mode-unassign"
          >
            <div className="flex items-center gap-2">
              <input
                type="radio"
                name="reassign-mode"
                value="unassign"
                checked={mode === 'unassign'}
                onChange={() => setMode('unassign')}
              />
              <span className="text-sm font-medium text-stone-900">Unassign</span>
            </div>
            <p className="text-xs text-stone-500 ml-6">
              Clear the source link on dependent orders (their source string stays, remappable in Source Mappings) and remove the name from any florist's source list.
            </p>
          </label>
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose} disabled={working}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="reassign-confirm-btn"
          >
            {working ? 'Working…' : 'Reassign & Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SourcesTab = ({ sources, controller, blocked, onOpenBreakout, onCloseBreakout }) => {
  const { showForm, setShowForm, editingId, form, setForm, resetForm: resetDraft, open, capture } = useOwnedForm(controller, () => ({ name: '', url: '', product_url: '' }));
  const [sortAsc, setSortAsc] = useState(true);
  const owns = useTabOwner(controller);
  const [armedDelete, confirmDelete, clearDelete] = useDeleteArm();
  const [reassignDialog, setReassignDialog] = useState(null); // { source, dependents }

  const sorted = [...sources].sort((a, b) => {
    const cmp = a.name.localeCompare(b.name);
    return sortAsc ? cmp : -cmp;
  });

  const resetForm = () => { resetDraft(); clearDelete(); setReassignDialog(null); };

  const openEdit = (n) => {
    open(n.id, { name: n.name, url: n.url || '', product_url: n.product_url || '' });
    clearDelete(); setReassignDialog(null);
  };

  const handleSubmit = async () => {
    if (blocked) return;
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const submitted = capture();
    const result = await controller.mutate(submitted.id ? 'source:update' : 'source:create', submitted.id, submitted.payload);
    if (!submitted.current(false)) return;
    reportMutation(result, submitted.id ? 'Source updated' : 'Source added');
    if (result.kind === 'accepted') {
      submitted.acceptId(result.value.id);
      if (submitted.current()) resetForm();
    }
  };

  const performDelete = async (id, body) => {
    if (blocked) return;
    const submitted = capture();
    const result = await controller.mutate('source:delete', id, body);
    if (!submitted.current(false)) return;
    const detail = result.detail;
    if (isSourceDependencyRefusal(result, id)) {
      const source = sources.find(item => item.id === id);
      if (source && submitted.current()) setReassignDialog({ source, dependents: detail.dependents, submitted });
      return;
    }
    reportMutation(result, 'Source deleted');
    if (result.kind === 'accepted') {
      setReassignDialog(null);
      if (submitted.current() && submitted.id === id) resetForm();
    }
  };

  const handleDelete = async (id) => {
    if (blocked || !confirmDelete(id)) return;
    await performDelete(id, null);
  };

  // The breakout edits the charges; the mutation stays here with every other
  // Source write, so outcome reporting and ownership are handled one way.
  const saveRules = async (id, deductionRuleSets) => {
    if (blocked) return false;
    const result = await controller.mutate('source:update', id, { deduction_rule_sets: deductionRuleSets });
    if (!owns()) return false;
    reportMutation(result, 'Charges saved');
    return result.kind === 'accepted';
  };

  const openRules = (source) => {
    onOpenBreakout?.(
      <DeductionRulesBreakout
        key={source.id}
        source={source}
        blocked={blocked}
        onSave={payload => saveRules(source.id, payload)}
        onClose={() => onCloseBreakout?.()}
      />,
    );
  };

  const otherSourcesForReassign = reassignDialog
    ? sources.filter(s => s.id !== reassignDialog.source.id).sort((a, b) => a.name.localeCompare(b.name))
    : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-stone-500">
          Channels that send orders to your shop — wire services like Teleflora and FTD, plus direct channels like phone and Stripe. Used by the Products catalog and Sending Florist mappings.
        </p>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="flex items-center gap-2" data-testid="add-source-btn">
          <Plus className="h-4 w-4" /> Add Source
        </Button>
      </div>

      {showForm && (
        <div className="border border-stone-200 rounded-xl bg-white p-5 space-y-4" data-testid="source-form">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-stone-900">{editingId ? 'Edit Source' : 'New Source'}</h3>
            <button onClick={resetForm} className="text-stone-400 hover:text-stone-600"><X className="h-4 w-4" /></button>
          </div>
          <div>
            <label className="text-xs font-medium text-stone-500 mb-1 block">Name *</label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g., Lovingly" data-testid="source-name-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-stone-500 mb-1 block">URL</label>
            <Input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="https://www.example.com" data-testid="source-url-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-stone-500 mb-1 block">Product URL</label>
            <Input value={form.product_url} onChange={e => setForm(f => ({ ...f, product_url: e.target.value }))} placeholder="https://www.example.com/product-catalog" data-testid="source-product-url-input" />
          </div>
          <div className="flex gap-2 justify-between items-center pt-2 border-t border-stone-100">
            {editingId ? (
              <Button
                variant={armedDelete === editingId ? 'destructive' : 'outline'}
                onClick={() => handleDelete(editingId)}
                className={armedDelete === editingId ? '' : 'text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700'}
                data-testid="source-delete-btn"
                disabled={blocked}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {armedDelete === editingId ? 'Click again to confirm' : 'Delete Source'}
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={resetForm}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={blocked} data-testid="source-save-btn">{editingId ? 'Update' : 'Create'}</Button>
            </div>
          </div>
        </div>
      )}

      {sources.length === 0 ? (
        <div className="text-center py-12 text-stone-400">
          <Radio className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No sources configured yet</p>
        </div>
      ) : (
        <div className="border border-stone-200 rounded-xl bg-white overflow-hidden">
          {/* Column headers */}
          <div className="grid grid-cols-[minmax(14rem,1fr)_2fr_auto] gap-4 px-4 py-2.5 bg-stone-50 border-b border-stone-200 text-[11px] font-semibold uppercase tracking-wider text-stone-500">
            <button
              onClick={() => setSortAsc(s => !s)}
              className="flex items-center gap-1.5 text-left hover:text-stone-700 transition-colors"
              data-testid="source-sort-toggle"
            >
              Name
              <ArrowUpDown className="h-3 w-3" />
              <span className="text-[10px] font-normal text-stone-400 normal-case">({sortAsc ? 'A–Z' : 'Z–A'})</span>
            </button>
            <span>URL</span>
            <span className="w-16 text-right">Actions</span>
          </div>

          <div className="divide-y divide-stone-100">
            {sorted.map(s => (
              <div key={s.id} className="grid grid-cols-[minmax(14rem,1fr)_2fr_auto] gap-4 px-4 py-3 items-center" data-testid={`source-row-${s.id}`}>
                {/* The name is the way in to this source's charges. Nothing
                    else on the row says so, so it has to look clickable. */}
                <button
                  type="button" onClick={() => openRules(s)} data-testid={`source-rules-${s.id}`}
                  title={`Charging rules for ${s.name}`}
                  className="text-sm font-medium text-stone-800 truncate text-left hover:text-primary hover:underline focus:underline"
                >{s.name}</button>
                <div className="min-w-0 text-xs">
                  {s.url ? (
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-stone-600 hover:text-primary truncate block">
                      {s.url}
                    </a>
                  ) : (
                    <span className="text-stone-300 italic">—</span>
                  )}
                </div>
                <div className="flex gap-1 justify-end">
                  <button onClick={() => openEdit(s)} className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors" data-testid={`edit-source-${s.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ReassignBeforeDeleteDialog
        open={!!reassignDialog}
        source={reassignDialog?.source}
        dependents={reassignDialog?.dependents}
        otherSources={otherSourcesForReassign}
        onClose={() => setReassignDialog(null)}
        onConfirm={(body) => { if (reassignDialog?.submitted.current()) performDelete(reassignDialog.source.id, body); else setReassignDialog(null); }}
        working={blocked || !reassignDialog?.submitted.current()}
      />
    </div>
  );
};

/* ── List A: Sending Florists ────────────────────────────── */
const FloristsTab = ({ florists, sources, controller, blocked }) => {
  const [search, setSearch] = useState('');
  const [sortAsc, setSortAsc] = useState(true);
  const { showForm, setShowForm, editingId, form, setForm, resetForm: resetDraft, open, capture } = useOwnedForm(controller, () => ({ name: '', network_sources: [], product_url: '', owns_catalog: false }));
  const [armedDelete, confirmDelete, clearDelete] = useDeleteArm();

  const resetForm = () => { resetDraft(); clearDelete(); };

  const openEdit = (f) => {
    open(f.id, { name: f.name, network_sources: f.network_sources || [], product_url: f.product_url || '', owns_catalog: f.owns_catalog || false });
    clearDelete();
  };

  const toggleNetwork = (net) => {
    setForm(f => ({
      ...f,
      network_sources: f.network_sources.includes(net)
        ? f.network_sources.filter(n => n !== net)
        : [...f.network_sources, net]
    }));
  };

  const handleSubmit = async () => {
    if (blocked) return;
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const submitted = capture();
    const result = await controller.mutate(submitted.id ? 'florist:update' : 'florist:create', submitted.id, submitted.payload);
    if (!submitted.current(false)) return;
    reportMutation(result, submitted.id ? 'Florist updated' : 'Florist added');
    if (result.kind === 'accepted') {
      submitted.acceptId(result.value.id);
      if (submitted.current()) resetForm();
    }
  };

  const handleDelete = async (id) => {
    if (blocked || !confirmDelete(id)) return;
    const submitted = capture();
    const result = await controller.mutate('florist:delete', id);
    if (!submitted.current(false)) return;
    reportMutation(result, 'Florist deleted');
    if (result.kind === 'accepted' && submitted.current() && submitted.id === id) resetForm();
  };

  const filtered = florists.filter(f => {
    if (!search) return true;
    const q = search.toLowerCase();
    return f.name.toLowerCase().includes(q) || (f.product_url || '').toLowerCase().includes(q);
  }).sort((a, b) => {
    const cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    return sortAsc ? cmp : -cmp;
  });

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-xs w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search florists..." className="pl-9" data-testid="florists-search" />
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="flex items-center gap-2" data-testid="add-florist-btn">
          <Plus className="h-4 w-4" /> Add Florist
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="border border-stone-200 rounded-xl bg-white p-5 space-y-4" data-testid="florist-form">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-stone-900">{editingId ? 'Edit Florist' : 'New Florist'}</h3>
            <button onClick={resetForm} className="text-stone-400 hover:text-stone-600"><X className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Name *</label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g., From You Flowers" data-testid="florist-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Product Lookup URL</label>
              <Input value={form.product_url} onChange={e => setForm(f => ({ ...f, product_url: e.target.value }))} placeholder="https://www.example.com" data-testid="florist-url-input" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-stone-500 mb-2 block">Sources</label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center justify-between gap-2 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm text-left hover:border-stone-300 focus:outline-none focus:ring-2 focus:ring-stone-400"
                  data-testid="florist-sources-trigger"
                >
                  <span className="truncate">
                    {form.network_sources.length === 0 ? (
                      <span className="text-stone-400">Select sources…</span>
                    ) : (
                      form.network_sources.slice().sort((a, b) => a.localeCompare(b)).join(', ')
                    )}
                  </span>
                  <ChevronDown className="h-4 w-4 text-stone-400 flex-shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[240px]" align="start">
                {sources.length === 0 ? (
                  <div className="p-3 text-xs text-stone-400 italic">Add sources in the Sources tab first.</div>
                ) : (
                  <ul className="max-h-64 overflow-y-auto py-1" data-testid="florist-sources-checklist">
                    {[...sources].sort((a, b) => a.name.localeCompare(b.name)).map(src => {
                      const checked = form.network_sources.includes(src.name);
                      return (
                        <li key={src.id}>
                          <label
                            className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-stone-50"
                            data-testid={`source-check-${src.name}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleNetwork(src.name)}
                            />
                            <span className="text-sm text-stone-700">{src.name}</span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </PopoverContent>
            </Popover>
          </div>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="florist-owns-catalog">
            <Checkbox
              checked={form.owns_catalog}
              onCheckedChange={(v) => setForm(f => ({ ...f, owns_catalog: v === true }))}
              className="mt-0.5"
            />
            <span className="text-sm text-stone-700">
              Has Catalog
              <span className="block text-xs text-stone-400">Check this florist's own products first when resolving an order's recipe &amp; image.</span>
            </span>
          </label>
          <div className="flex gap-2 justify-between items-center pt-2 border-t border-stone-100">
            {editingId ? (
              <Button
                variant={armedDelete === editingId ? 'destructive' : 'outline'}
                onClick={() => handleDelete(editingId)}
                className={armedDelete === editingId ? '' : 'text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700'}
                data-testid="florist-delete-btn"
                disabled={blocked}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {armedDelete === editingId ? 'Click again to confirm' : 'Delete Florist'}
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={resetForm}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={blocked} data-testid="florist-save-btn">{editingId ? 'Update' : 'Create'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-stone-400">
          <Flower2 className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">{florists.length === 0 ? 'No sending florists yet' : 'No matches'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Column heading */}
          <div className="px-4 py-1.5">
            <button
              onClick={() => setSortAsc(prev => !prev)}
              className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-stone-400 hover:text-stone-600 transition-colors"
              data-testid="sort-florist-name"
            >
              Name
              <ArrowUpDown className="h-3 w-3" />
              <span className="text-[9px] font-normal normal-case tracking-normal">({sortAsc ? 'A-Z' : 'Z-A'})</span>
            </button>
          </div>
          {filtered.map(f => (
            <div key={f.id} className="flex items-center gap-4 px-4 py-3 bg-white border border-stone-200 rounded-xl" data-testid={`florist-${f.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-stone-800">{f.name}</span>
                  {f.owns_catalog && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">Has Catalog</span>
                  )}
                  {(f.network_sources || []).map(n => (
                    <span key={n} className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-500 font-medium">{n}</span>
                  ))}
                </div>
                {f.product_url && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <Link2 className="h-3 w-3 text-stone-400" />
                    <span className="text-xs text-stone-400 truncate">{f.product_url}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => openEdit(f)} className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors" data-testid={`edit-florist-${f.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/* ── List B: AI Mappings ─────────────────────────────────── */
const MappingsTab = ({ mappings, florists, controller, blocked }) => {
  const owns = useTabOwner(controller);
  const [search, setSearch] = useState('');
  const [sortAsc, setSortAsc] = useState(true);
  const [floristFilter, setFloristFilter] = useState('all'); // 'all', 'unmapped', or a florist id
  const [armedDelete, confirmDelete] = useDeleteArm();
  const [selectedMapping, setSelectedMapping] = useState(null); // opens the orders side panel

  const handleMap = async (mappingId, floristId) => {
    if (blocked) return;
    const result = await controller.mutate('mapping:update', mappingId, { sending_florist_id: floristId || null });
    if (owns()) reportMutation(result, 'Mapping updated');
  };

  const handleDelete = async (mappingId) => {
    if (blocked || !confirmDelete(mappingId)) return;
    const result = await controller.mutate('mapping:delete', mappingId);
    if (owns()) reportMutation(result, 'Mapping deleted');
  };

  const filtered = mappings
    .filter(m => {
      if (search) {
        const q = search.toLowerCase();
        if (!m.extracted_string.toLowerCase().includes(q) && !(m.sending_florist_name || '').toLowerCase().includes(q)) return false;
      }
      if (floristFilter === 'unmapped') return !m.sending_florist_id;
      if (floristFilter !== 'all') return m.sending_florist_id === floristFilter;
      return true;
    })
    .sort((a, b) => {
      const cmp = a.extracted_string.localeCompare(b.extracted_string, undefined, { sensitivity: 'base' });
      return sortAsc ? cmp : -cmp;
    });

  const unmappedCount = mappings.filter(m => !m.sending_florist_id).length;

  return (
    <div className="space-y-4">
      {/* Header info */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-xs w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search extracted strings..." className="pl-9" data-testid="mappings-search" />
        </div>
        {unmappedCount > 0 && (
          <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200">
            {unmappedCount} unmapped
          </span>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-stone-400">
          <p className="text-sm">{mappings.length === 0 ? 'No AI extractions yet — they appear here as orders are processed' : 'No matches'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Column headings */}
          <div className="flex items-center gap-3 px-4 py-1.5">
            <button
              onClick={() => setSortAsc(prev => !prev)}
              className="flex-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-stone-400 hover:text-stone-600 transition-colors"
              data-testid="sort-extracted-string"
            >
              Extracted String
              <ArrowUpDown className="h-3 w-3" />
              <span className="text-[9px] font-normal normal-case tracking-normal">({sortAsc ? 'A-Z' : 'Z-A'})</span>
            </button>
            <div className="w-4" />
            <div className="w-48">
              <select
                value={floristFilter}
                onChange={e => setFloristFilter(e.target.value)}
                className="w-full text-[10px] font-bold uppercase tracking-widest text-stone-400 bg-transparent border-none focus:outline-none cursor-pointer"
                data-testid="filter-assigned-florist"
              >
                <option value="all">Assigned Florist (All)</option>
                <option value="unmapped">Unmapped Only</option>
                {florists.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          </div>
          {filtered.map(m => {
            return (
            <div
              key={m.id}
              className={`bg-white border rounded-xl overflow-hidden transition-colors ${
                m.sending_florist_id ? 'border-stone-200' : 'border-amber-200 bg-amber-50/20'
              }`}
              data-testid={`mapping-${m.id}`}
            >
              <div className="flex items-center gap-3 px-4 py-3">
                {/* Extracted string */}
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-mono text-stone-700 truncate block" data-testid={`mapping-string-${m.id}`}>
                    {m.extracted_string}
                  </span>
                </div>

                {/* Order count — click to view the orders behind this string */}
                <button
                  onClick={() => setSelectedMapping(m)}
                  disabled={!m.order_count}
                  className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                    m.order_count
                      ? 'text-stone-600 bg-stone-100 hover:bg-stone-200'
                      : 'text-stone-300 cursor-default'
                  }`}
                  title={m.order_count ? 'View orders using this string' : 'No orders use this string'}
                  data-testid={`mapping-orders-toggle-${m.id}`}
                >
                  {m.order_count || 0} order{m.order_count === 1 ? '' : 's'}
                </button>

                <ArrowRight className="h-4 w-4 text-stone-300 flex-shrink-0" />

                {/* Florist dropdown */}
                <div className="w-48 flex-shrink-0">
                  <select
                    value={m.sending_florist_id || ''}
                    onChange={e => handleMap(m.id, e.target.value)}
                    className={`w-full px-3 py-1.5 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                      m.sending_florist_id ? 'border-stone-200 text-stone-700' : 'border-amber-300 text-amber-700'
                    }`}
                    data-testid={`mapping-select-${m.id}`}
                    disabled={blocked}
                  >
                    <option value="">— Unassigned —</option>
                    {florists.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={() => handleDelete(m.id)}
                  className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
                    armedDelete === m.id
                      ? 'bg-red-500 text-white hover:bg-red-600'
                      : 'hover:bg-red-50 text-stone-400 hover:text-red-500'
                  }`}
                  title={armedDelete === m.id ? 'Click again to confirm' : 'Delete mapping'}
                  data-testid={`mapping-delete-${m.id}`}
                  disabled={blocked}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>

            </div>
            );
          })}
        </div>
      )}

      <MappingOrdersSheet
        key={selectedMapping?.id || 'closed'}
        controller={controller}
        mapping={selectedMapping}
        mappedToName={selectedMapping?.sending_florist_name}
        ordersPath="subsource-mappings"
        onClose={() => setSelectedMapping(null)}
      />
    </div>
  );
};


/* ── Source Mappings (SM-1) ──────────────────────────────────
   Twin of the AI Mappings tab above, but for order sources instead of sending
   florists: raw `network_source` strings → curated Sources. An order's displayed
   source is resolved live through these mappings, so mapping a string here
   updates every order that carries it. Backend: /api/source-mappings. */
const SourceMappingsTab = ({ mappings, sources, controller, blocked }) => {
  const owns = useTabOwner(controller);
  const [search, setSearch] = useState('');
  const [sortAsc, setSortAsc] = useState(true);
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all', 'unmapped', or a source id
  const [armedDelete, confirmDelete] = useDeleteArm();
  const [selectedMapping, setSelectedMapping] = useState(null); // opens the orders side panel

  const handleMap = async (mappingId, sourceId) => {
    if (blocked) return;
    const result = await controller.mutate('sourceMapping:update', mappingId, { source_id: sourceId || null });
    if (owns()) reportMutation(result, 'Mapping updated');
  };

  const handleDelete = async (mappingId) => {
    if (blocked || !confirmDelete(mappingId)) return;
    const result = await controller.mutate('sourceMapping:delete', mappingId);
    if (owns()) reportMutation(result, 'Mapping deleted');
  };

  const filtered = mappings
    .filter(m => {
      if (search) {
        const q = search.toLowerCase();
        if (!m.extracted_string.toLowerCase().includes(q) && !(m.source_name || '').toLowerCase().includes(q)) return false;
      }
      if (sourceFilter === 'unmapped') return !m.source_id;
      if (sourceFilter !== 'all') return m.source_id === sourceFilter;
      return true;
    })
    .sort((a, b) => {
      const cmp = a.extracted_string.localeCompare(b.extracted_string, undefined, { sensitivity: 'base' });
      return sortAsc ? cmp : -cmp;
    });

  const unmappedCount = mappings.filter(m => !m.source_id).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-xs w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search extracted strings..." className="pl-9" data-testid="source-mappings-search" />
        </div>
        {unmappedCount > 0 && (
          <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200">
            {unmappedCount} unmapped
          </span>
        )}
      </div>

      {mappings.length === 0 ? (
        <div className="text-center py-12 text-stone-400">
          <p className="text-sm">No source strings yet — they appear here as orders are processed</p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-3 px-4 py-1.5">
            <button
              onClick={() => setSortAsc(prev => !prev)}
              className="flex-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-stone-400 hover:text-stone-600 transition-colors"
              data-testid="source-sort-extracted-string"
            >
              Extracted String
              <ArrowUpDown className="h-3 w-3" />
              <span className="text-[9px] font-normal normal-case tracking-normal">({sortAsc ? 'A-Z' : 'Z-A'})</span>
            </button>
            <div className="w-4" />
            <div className="w-48">
              <select
                value={sourceFilter}
                onChange={e => setSourceFilter(e.target.value)}
                className="w-full text-[10px] font-bold uppercase tracking-widest text-stone-400 bg-transparent border-none focus:outline-none cursor-pointer"
                data-testid="filter-assigned-source"
              >
                <option value="all">Assigned Source (All)</option>
                <option value="unmapped">Unmapped Only</option>
                {sources.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>
          {filtered.length === 0 ? (
            <div className="text-center py-8 text-stone-400">
              <p className="text-sm">No matches</p>
            </div>
          ) : filtered.map(m => {
            return (
            <div
              key={m.id}
              className={`bg-white border rounded-xl overflow-hidden transition-colors ${
                m.source_id ? 'border-stone-200' : 'border-amber-200 bg-amber-50/20'
              }`}
              data-testid={`source-mapping-${m.id}`}
            >
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-mono text-stone-700 truncate block" data-testid={`source-mapping-string-${m.id}`}>
                    {m.extracted_string}
                  </span>
                </div>

                <button
                  onClick={() => setSelectedMapping(m)}
                  disabled={!m.order_count}
                  className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                    m.order_count
                      ? 'text-stone-600 bg-stone-100 hover:bg-stone-200'
                      : 'text-stone-300 cursor-default'
                  }`}
                  title={m.order_count ? 'View orders using this string' : 'No orders use this string'}
                  data-testid={`source-mapping-orders-toggle-${m.id}`}
                >
                  {m.order_count || 0} order{m.order_count === 1 ? '' : 's'}
                </button>

                <ArrowRight className="h-4 w-4 text-stone-300 flex-shrink-0" />

                <div className="w-48 flex-shrink-0">
                  <select
                    value={m.source_id || ''}
                    onChange={e => handleMap(m.id, e.target.value)}
                    className={`w-full px-3 py-1.5 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                      m.source_id ? 'border-stone-200 text-stone-700' : 'border-amber-300 text-amber-700'
                    }`}
                    data-testid={`source-mapping-select-${m.id}`}
                    disabled={blocked}
                  >
                    <option value="">— Unassigned —</option>
                    {sources.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={() => handleDelete(m.id)}
                  className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${
                    armedDelete === m.id
                      ? 'bg-red-500 text-white hover:bg-red-600'
                      : 'hover:bg-red-50 text-stone-400 hover:text-red-500'
                  }`}
                  title={armedDelete === m.id ? 'Click again to confirm' : 'Delete mapping'}
                  data-testid={`source-mapping-delete-${m.id}`}
                  disabled={blocked}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>

            </div>
            );
          })}
        </div>
      )}

      <MappingOrdersSheet
        key={selectedMapping?.id || 'closed'}
        controller={controller}
        mapping={selectedMapping}
        mappedToName={selectedMapping?.source_name}
        ordersPath="source-mappings"
        onClose={() => setSelectedMapping(null)}
      />
    </div>
  );
};


/* ── Orders side panel for a source mapping (SM-1) ────────────
   Opened by clicking a mapping's order-count. Lists orders that came in for that
   raw source string, filtered by the order's arrival date. Defaults to the last
   30 days; the window is capped at 30 days and can't reach before the oldest
   matching order. */
function MappingOrdersSheet({ mapping, mappedToName, ordersPath = 'source-mappings', onClose, controller }) {
  const [range, setRange] = useState(null);       // { from, to } Date objects
  const [earliest, setEarliest] = useState(null); // Date of oldest matching order
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [readError, setReadError] = useState('');
  const [detailOrderId, setDetailOrderId] = useState(null); // order shown in the read-only detail popup
  const request = useRef({ sequence: 0, abort: null, active: true });
  const retireRead = useCallback(() => {
    ++request.current.sequence;
    request.current.abort?.abort();
  }, []);
  useLayoutEffect(() => {
    const owner = request.current;
    owner.active = true;
    return () => { owner.active = false; retireRead(); };
  }, [retireRead]);

  const fetchOrders = useCallback(async (from, to) => {
    if (!mapping || !from || !to) return;
    retireRead();
    const sequence = request.current.sequence;
    const abort = new AbortController();
    request.current.abort = abort;
    const current = () => request.current.active && request.current.sequence === sequence && controller.owns();
    setLoading(true);
    setOrders([]); setTotal(null); setEarliest(null); setDetailOrderId(null); setReadError('');
    try {
      const data = await controller.read(config => getMappingOrders(ordersPath, mapping.id, {
        start: format(from, 'yyyy-MM-dd'),
        end: format(to, 'yyyy-MM-dd'),
      }, config), { isCurrent: current, signal: abort.signal });
      if (!current()) return;
      const validOrder = row => row && typeof row.id === 'string' && !!row.id
        && ['internal_order_number', 'order_number'].every(field => row[field] == null || typeof row[field] === 'string' || (typeof row[field] === 'number' && Number.isFinite(row[field])))
        && ['recipient_name', 'order_accepted_date', 'status'].every(field => row[field] == null || typeof row[field] === 'string');
      if (!Array.isArray(data?.orders) || !data.orders.every(validOrder)
        || new Set(data.orders.map(row => row.id)).size !== data.orders.length
        || !Number.isSafeInteger(data.total) || data.total < data.orders.length
        || (data.earliest != null && (typeof data.earliest !== 'string' || !Number.isFinite(parseISO(data.earliest).getTime())))) throw new Error('Invalid history');
      setOrders(data.orders);
      setTotal(data.total);
      setEarliest(data.earliest ? parseISO(data.earliest) : null);
    } catch {
      if (current()) setReadError('Orders for this mapping and range could not be established. Retry the read.');
    } finally {
      if (current()) setLoading(false);
    }
  }, [mapping, ordersPath, controller, retireRead]);

  // On open (or when a different mapping is picked), default to the last 30 days.
  useEffect(() => {
    if (!mapping) return;
    const to = new Date();
    const from = subDays(to, 29); // 30 days inclusive
    setRange({ from, to });
    fetchOrders(from, to);
  }, [mapping, fetchOrders]);

  // Selection is handled by the shared <RangeCalendar>; here we just apply a
  // completed range immediately (fetch its orders).
  const handleRangeChange = (next) => {
    retireRead(); setOrders([]); setTotal(null); setDetailOrderId(null); setReadError(''); setLoading(false);
    setRange(next);
    if (next?.from && next?.to) fetchOrders(next.from, next.to);
  };

  return (
    <>
    <Sheet open={!!mapping} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col" data-testid="mapping-orders-sheet">
        <SheetHeader className="space-y-1 text-left">
          <SheetTitle className="font-mono text-sm break-all">{mapping?.extracted_string}</SheetTitle>
          <SheetDescription className="text-xs text-stone-500">
            {mappedToName ? `Mapped to ${mappedToName}` : 'Unmapped'} · {total === null ? 'Order count not established' : `${total} order${total === 1 ? '' : 's'} in range`}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="mapping-orders-daterange">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {range?.from && range?.to
                  ? `${format(range.from, 'MMM d, yyyy')} — ${format(range.to, 'MMM d, yyyy')}`
                  : range?.from
                    ? `${format(range.from, 'MMM d, yyyy')} — …`
                    : 'Pick a date range'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <RangeCalendar
                value={range}
                onChange={handleRangeChange}
                numberOfMonths={1}
                disabledBefore={earliest}
                disabledAfter={new Date()}
                maxSpanDays={30}
              />
              <p className="px-3 pb-2 text-[11px] text-stone-400">Up to 30 days at a time · click any date to restart.</p>
            </PopoverContent>
          </Popover>
        </div>

        <div className="mt-4 flex-1 overflow-y-auto space-y-1.5 pr-1">
          {loading ? (
            <p className="text-xs text-stone-400 text-center py-6">Loading…</p>
          ) : readError ? (
            <div role="status"><p>{readError}</p><Button onClick={() => fetchOrders(range?.from, range?.to)}>Retry history</Button></div>
          ) : total === null ? (
            <p className="text-xs text-stone-400 text-center py-6">Select a complete date range.</p>
          ) : orders.length === 0 ? (
            <p className="text-xs text-stone-400 text-center py-6">No orders in this range.</p>
          ) : (
            <>
              {total > orders.length && (
                <p className="text-[11px] text-stone-400">Showing {orders.length} of {total}.</p>
              )}
              {orders.map(o => (
                <div
                  key={o.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setDetailOrderId(o.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailOrderId(o.id); } }}
                  className="flex items-center gap-3 text-xs bg-white border border-stone-100 rounded-lg px-3 py-2 cursor-pointer hover:bg-stone-50 hover:border-stone-200 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors"
                  title="View order details"
                  data-testid={`mapping-sheet-order-${o.id}`}
                >
                  <span className="font-mono text-stone-500 flex-shrink-0">{o.internal_order_number || o.order_number || '—'}</span>
                  <span className="text-stone-700 truncate flex-1 min-w-0">{o.recipient_name || '—'}</span>
                  <span className="text-stone-400 flex-shrink-0">{o.order_accepted_date || '—'}</span>
                  <span className="text-stone-400 flex-shrink-0 capitalize">{o.status || '—'}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
    {detailOrderId && (
      <OrderDetailModal orderId={detailOrderId} onClose={() => setDetailOrderId(null)} />
    )}
    </>
  );
}


/* ── Main Panel ──────────────────────────────────────────── */
const SendingFloristsPanel = ({ onOpenBreakout, onCloseBreakout }) => {
  const catalog = useSourcesCatalog();
  return <SourcesCatalogBody key={catalog.controller.key} {...catalog} onOpenBreakout={onOpenBreakout} onCloseBreakout={onCloseBreakout} />;
};

function SourcesCatalogBody({ controller, data, state, notice, active, pending, unknown, reviewable, reviewRecord, onOpenBreakout, onCloseBreakout }) {
  if (!active) return <p role="status" data-testid="sources-notice">{notice || 'Sources & Senders requires the current cloud administrator session.'}</p>;
  const blocked = pending || unknown || state !== 'available';
  const feedback = <div className="space-y-2" role="status">
    {notice && <p data-testid="sources-notice">{notice}</p>}
    {state === 'loading' && <p>Loading current Sources & Senders…</p>}
    {state === 'unavailable' && !pending && <p data-testid="sources-unavailable">Current Sources & Senders could not be established. {data ? 'Previously read information remains visible; changes are unavailable until refreshed.' : 'No empty catalog has been established.'}</p>}
    {pending && <p>A management change is still pending.</p>}
    {/* TCA-26102604: the popup says this when it is showing. This line is for
        after it is dismissed, or for a write made from another tab. */}
    {unknown && !notice && <p data-testid="sources-unconfirmed">This change could not be confirmed. Check the list before changing it again.</p>}
    <Button variant="outline" onClick={() => controller.refresh()} disabled={pending} data-testid="sources-refresh">Refresh current data</Button>
    {reviewable && reviewRecord && <UnconfirmedChangeDialog record={reviewRecord} onDismiss={() => controller.reviewUnknown()} />}
  </div>;
  if (!data) return feedback;
  const { sources, florists, mappings, sourceMappings } = data;

  const unmappedCount = mappings.filter(m => !m.sending_florist_id).length;
  const sourceUnmappedCount = sourceMappings.filter(m => !m.source_id).length;

  return (
    <div className="space-y-4" data-testid="sources-senders-section">
      {feedback}
      <Tabs defaultValue="florists" className="w-full">
        <TabsList className="bg-stone-100/50">
          <TabsTrigger value="florists" className="data-[state=active]:bg-white" data-testid="florists-tab">
            Sending Florists
          </TabsTrigger>
          <TabsTrigger value="mappings" className="data-[state=active]:bg-white" data-testid="mappings-tab">
            AI Mappings
            {unmappedCount > 0 && (
              <span className="ml-1.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold bg-rose-500 text-white rounded-full px-1">
                {unmappedCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="sources"
            className="data-[state=active]:bg-white data-[state=active]:text-amber-700 text-amber-600/70 flex items-center gap-1.5"
            data-testid="sources-tab"
          >
            <Radio className="h-3.5 w-3.5" />
            Sources
          </TabsTrigger>
          <TabsTrigger
            value="source-mappings"
            className="data-[state=active]:bg-white data-[state=active]:text-amber-700 text-amber-600/70 flex items-center gap-1.5"
            data-testid="source-mappings-tab"
          >
            <Radio className="h-3.5 w-3.5" />
            Source Mappings
            {sourceUnmappedCount > 0 && (
              <span className="ml-1 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold bg-rose-500 text-white rounded-full px-1">
                {sourceUnmappedCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sources" className="mt-4">
          <SourcesTab sources={sources} controller={controller} blocked={blocked} onOpenBreakout={onOpenBreakout} onCloseBreakout={onCloseBreakout} />
        </TabsContent>

        <TabsContent value="florists" className="mt-4">
          <FloristsTab florists={florists} sources={sources} controller={controller} blocked={blocked} />
        </TabsContent>

        <TabsContent value="mappings" className="mt-4">
          <MappingsTab mappings={mappings} florists={florists} controller={controller} blocked={blocked} />
        </TabsContent>

        <TabsContent value="source-mappings" className="mt-4">
          <SourceMappingsTab mappings={sourceMappings} sources={sources} controller={controller} blocked={blocked} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * A change whose outcome the app could not establish — TCA-26102604.
 *
 * It may have saved and it may not have, and repeating it would apply it
 * twice if it did. So the popup shows what is stored right now and asks the
 * only question a person can answer: is that what you wanted?
 *
 * Dismissing it is the acknowledgement. There is deliberately no second
 * button, and no way to close this without releasing the hold — a popup that
 * can be closed without effect reappears, and teaches people to close it
 * faster. The cost, accepted by the owner, is that nobody is made to read it.
 */
function UnconfirmedChangeDialog({ record, onDismiss }) {
  const { kind, value, failure, submitted } = record;
  const [subject] = kind.split(':');
  const label = { source: 'source', florist: 'sending florist', mapping: 'AI mapping', sourceMapping: 'source mapping' }[subject];
  const target = subject === 'mapping' ? value.sending_florist_name || value.sending_florist_id
    : value.source_name || value.source_id;
  const diagnostic = unconfirmedDiagnostic({
    kind, id: record.id, failure, submitted, storedNow: value, backend: API,
  });
  const details = JSON.stringify(diagnostic, null, 2);
  const [copied, setCopied] = useState(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(details);
      setCopied('done');
    } catch {
      // Shown rather than lost: a copy button that silently fails leaves the
      // one person who could relay the fault with nothing to relay.
      setCopied('manual');
    }
  };

  return <AlertDialog open onOpenChange={open => { if (!open) onDismiss(); }}>
    <AlertDialogContent data-testid="sources-unconfirmed-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>We couldn&apos;t confirm your change saved</AlertDialogTitle>
        <AlertDialogDescription>
          It may have gone through, or it may not have. Don&apos;t enter it again until you&apos;ve checked.
        </AlertDialogDescription>
      </AlertDialogHeader>

      <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 space-y-1 text-sm">
        <p className="text-xs uppercase tracking-wider text-stone-500">What&apos;s stored for this {label} right now</p>
        <p className="font-medium text-stone-900">{value.name || value.extracted_string}</p>
        {subject === 'source' && <p className="text-stone-600">Website: {value.url || 'none'} · Product page: {value.product_url || 'none'}</p>}
        {subject === 'florist' && <>
          <p className="text-stone-600">Sources: {value.network_sources?.join(', ') || 'none'} · Own catalog: {value.owns_catalog ? 'Yes' : 'No'}</p>
          <p className="text-stone-600">Product page: {value.product_url || 'none'}</p>
        </>}
        {(subject === 'mapping' || subject === 'sourceMapping') && <p className="text-stone-600">Goes to: {target || 'Unassigned'}</p>}
      </div>

      <p className="text-sm text-stone-600">
        If that is already what you wanted, you&apos;re done. If not, make the change again after closing this.
      </p>
      <p className="text-xs text-stone-500">
        Error code: <span className="font-mono font-semibold text-stone-700" data-testid="sources-unconfirmed-code">{diagnostic.code}</span>
      </p>
      {copied === 'manual' && (
        <textarea
          readOnly value={details} rows={6} aria-label="Error details to copy"
          data-testid="sources-unconfirmed-details"
          className="w-full text-xs font-mono border border-stone-200 rounded-md p-2"
          onFocus={event => event.target.select()}
        />
      )}

      <AlertDialogFooter>
        <Button variant="outline" onClick={copy} data-testid="sources-copy-details">
          {copied === 'done' ? 'Copied' : 'Copy details'}
        </Button>
        <AlertDialogAction data-testid="sources-dismiss-unconfirmed">Dismiss</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>;
}

export default SendingFloristsPanel;
